import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function ContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: 'お問い合わせ | 無料見積もり実施中 | おかやま屋根工事株式会社 | 岡山市',
    description: '岡山市の屋根工事のご相談・無料見積もりはこちら。お電話（086-012-3456）またはお問い合わせフォームからお気軽にご連絡ください。営業時間9:00〜18:00。雨漏り修理、屋根葺き替え、屋根塗装など何でもご相談ください。',
    keywords: '岡山市,屋根工事,お問い合わせ,無料見積もり,相談,電話,フォーム',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema('お問い合わせ', '岡山市の屋根工事のご相談・無料見積もりはこちら。お電話またはお問い合わせフォームからお気軽にご連絡ください。'),
        generateBreadcrumbSchema([
          { name: 'ホーム', url: '/' },
          { name: 'お問い合わせ', url: '/contact' }
        ])
      ]
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5jf44qgbpr25eot47b0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/00a93b87dc95ad567127c37e4baf9f01.jpeg"
            alt="お問い合わせ"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">お問い合わせ</h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
            お気軽にご相談ください。無料見積もり実施中
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-16">
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#ff6b35] rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-phone-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">お電話でのお問い合わせ</h3>
              <a href="tel:0860123456" className="block text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-2 hover:text-[#ff6b35] transition-colors cursor-pointer">
                  086-012-3456
                </p>
              </a>
              <p className="text-gray-600 text-center text-sm sm:text-base">
                営業時間: 9:00〜18:00<br />
                お気軽にお電話ください
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-mail-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">FAX</h3>
              <p className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-2 text-center">
                086-012-3457
              </p>
              <p className="text-gray-600 text-center text-sm sm:text-base">
                24時間受付<br />
                FAXでもお問い合わせ可能です
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-map-pin-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">所在地</h3>
              <p className="text-gray-900 font-semibold mb-2 text-center text-sm sm:text-base">
                〒700-0000<br />
                岡山市北区○○町1-2-3
              </p>
              <p className="text-gray-600 text-center text-sm sm:text-base">
                駐車場完備<br />
                お気軽にお越しください
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">お問い合わせフォーム</h2>
            <p className="text-base sm:text-lg text-gray-600">
              以下のフォームに必要事項をご記入の上、送信してください
            </p>
          </div>
          
          <form 
            id="contact-form" 
            data-readdy-form 
            onSubmit={handleSubmit} 
            className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 lg:p-10"
          >
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-base font-bold text-gray-900 mb-3">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent text-sm sm:text-base"
                  placeholder="山田 太郎"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-base font-bold text-gray-900 mb-3">
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent text-sm sm:text-base"
                  placeholder="090-1234-5678"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-base font-bold text-gray-900 mb-3">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent text-sm sm:text-base"
                  placeholder="example@email.com"
                />
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-base font-bold text-gray-900 mb-3">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent text-sm sm:text-base cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="見積もり依頼">見積もり依頼</option>
                  <option value="雨漏り修理">雨漏り修理</option>
                  <option value="屋根葺き替え">屋根葺き替え</option>
                  <option value="屋根塗装">屋根塗装</option>
                  <option value="屋根点検">屋根点検</option>
                  <option value="その他">その他</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-base font-bold text-gray-900 mb-3">
                  詳細・ご要望 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent resize-none text-sm sm:text-base"
                  placeholder="お問い合わせ内容を詳しくご記入ください（500文字以内）"
                ></textarea>
                <p className="text-sm text-gray-500 mt-2 text-right">
                  {formData.message.length} / 500文字
                </p>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-[#1e3a5f] text-white text-base sm:text-lg font-bold rounded-lg hover:bg-[#152a45] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
            </div>
            
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-center font-semibold">
                  お問い合わせありがとうございます。<br />
                  担当者より折り返しご連絡させていただきます。
                </p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-center font-semibold">
                  送信に失敗しました。<br />
                  お手数ですが、お電話にてお問い合わせください。
                </p>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">よくある質問</h2>
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            <FAQItem
              question="見積もりは無料ですか？"
              answer="はい、お見積もりは完全無料です。現地調査を行い、詳細なお見積もりをご提示いたします。お見積もり後のキャンセルも可能ですので、お気軽にご相談ください。"
            />
            <FAQItem
              question="工事期間はどのくらいかかりますか？"
              answer="工事内容により異なりますが、一般的な屋根塗装で3〜5日、葺き替え工事で7〜10日程度です。雨漏り修理は1〜2日で完了することが多いです。詳しくは現地調査時にご説明いたします。"
            />
            <FAQItem
              question="雨の日でも工事は可能ですか？"
              answer="基本的に雨天時は工事を中止いたします。お客様の大切な住まいを守るため、天候を見ながら安全に作業を進めます。工期が延びる場合は事前にご連絡いたします。"
            />
            <FAQItem
              question="保証内容について教えてください"
              answer="工事内容に応じて最長10年の保証をご用意しております。施工不良による雨漏りや不具合が発生した場合、無償で修理対応いたします。保証書を発行し、アフターフォローも万全です。"
            />
            <FAQItem
              question="支払い方法は何がありますか？"
              answer="現金、銀行振込、各種クレジットカードに対応しております。また、リフォームローンのご相談も承っておりますので、お気軽にお問い合わせください。"
            />
            <FAQItem
              question="岡山市以外でも対応可能ですか？"
              answer="岡山市を中心に、近隣地域も対応しております。詳しい対応エリアについては、お電話またはお問い合わせフォームよりご相談ください。"
            />
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">アクセス</h2>
            <p className="text-base sm:text-lg text-gray-600">お気軽にお越しください</p>
          </div>
          
          <div className="bg-gray-100 rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52634.64498084778!2d133.88235!3d34.66167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355402e59fffffff%3A0x4c1b4c8c8c8c8c8c!2z5bKh5bGx5biC5YyX5Yy6!5e0!3m2!1sja!2sjp!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="おかやま屋根工事株式会社 地図"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
      >
        <span className="font-bold text-gray-900 pr-4 text-base sm:text-lg">{question}</span>
        <i className={`ri-arrow-down-s-line text-2xl text-[#1e3a5f] transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>
      {isOpen && (
        <div className="px-6 sm:px-8 pb-5 sm:pb-6">
          <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{answer}</p>
        </div>
      )}
    </div>
  );
}
